import { useState, useContext } from 'react'
import { ClipboardList, Pin, Star, MoreVertical, ExternalLink, Plus, Pen, CircleX, Trash2, Check, Smile } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { StatusBadge } from '@/pages/timeline/components/StatusBadge'
import { PostTerritories } from '@/pages/timeline/components/PostTerritories'
import { PostContexts } from '@/pages/timeline/components/PostContexts'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import { TimelinePost } from '@/types/post';
import { PostActions } from '@/hooks/use-post-actions'
import { TimelineContext } from '@/contexts/timeline'
import { CurationContext } from '@/contexts/curation'
import { RejectModal } from '@/pages/timeline/components/RejectModal'
import { ApprovePostModal } from '@/components/PostCard/components/ApprovePostModal'
import { Badge } from '@/components/ui/badge'

interface PostHeaderProps {
  status: 'pending' | 'approved' | 'rejected'
  post: TimelinePost
  actions: PostActions
  onEdit: () => void
}

export function PostHeader({ status, post, actions, onEdit }: PostHeaderProps) {
  const { handlePin, handleFavorite, handleReject, handleApprove, isPinning, isFavoriting, isRejecting, isApproving } = actions
  const { toast } = useToast()
  const timelineContext = useContext(TimelineContext)
  const curationContext = useContext(CurationContext)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
  const isInCuration = !!curationContext && !timelineContext

  const removePost = (postId: string) => {
    if (timelineContext) {
      timelineContext.removePost(postId)
    } else if (curationContext) {
      curationContext.removePost(postId)
    }
  }

  const description = post.properties?.description || post.metadata?.description || ''
  const hasDescription = Boolean(description)

  const handleRejectConfirm = async (_reasons: string[], _otherReason?: string) => {
    await handleReject()
    setIsRejectModalOpen(false)
  }

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja deletar este conteúdo?')) return
    
    setIsDeleting(true)
    try {
      removePost(post.id)
      toast({
        title: 'Conteúdo deletado',
        description: 'O conteúdo foi deletado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro ao deletar',
        description: 'Não foi possível deletar o conteúdo. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAddDescription = () => {
    onEdit()
  }

  const showTerritories = status === 'approved' && post.territories && post.territories.length > 0
  const showContexts = status === 'approved' && post.contexts && post.contexts.length > 0
  
  const mockTerritoryIds = ['territory-1', 'territory-2', 'territory-3', 'territory-4']
  const showMockTerritories = isInCuration && status === 'pending'

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {showTerritories && post.territories ? (
          <PostTerritories territoryIds={post.territories} />
        ) : showMockTerritories ? (
          <>
            <PostTerritories territoryIds={mockTerritoryIds} />
            <Badge className="text-black flex items-center gap-1.5 px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3FF2C' }}>
              <Smile className="w-3 h-3" />
              <Check className="w-3 h-3" />
            </Badge>
          </>
        ) : (
          <StatusBadge status={status} />
        )}
        {showContexts && post.contexts && (
          <PostContexts contextIds={post.contexts} />
        )}
      </div>
      <TooltipProvider delayDuration={0}>
        <div className="flex items-center gap-2">
          {!isInCuration && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <ClipboardList 
                    className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700" 
                    onClick={hasDescription ? undefined : handleAddDescription}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="!bg-black !text-white max-w-md rounded-lg py-1.5 px-3">
                  <p className="text-sm whitespace-pre-wrap">
                    <span className="font-semibold">Descrição:</span> {description || post.content}
                  </p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Pin 
                    className={`w-4 h-4 cursor-pointer transition-all ${
                      isPinning ? 'opacity-50 cursor-not-allowed' : ''
                    } ${
                      post.is_pinned 
                        ? 'text-black hover:text-black' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    fill={post.is_pinned ? 'currentColor' : 'none'}
                    onClick={!isPinning ? handlePin : undefined}
                  />
                </TooltipTrigger>
                <TooltipContent className="bg-black text-white">
                  <p>{post.is_pinned ? 'Desfixar post' : 'Fixar post'}</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Star 
                className={`w-4 h-4 cursor-pointer transition-all ${
                  isFavoriting ? 'opacity-50 cursor-not-allowed' : ''
                } ${
                  post.is_favorited 
                    ? 'text-black hover:text-black' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                fill={post.is_favorited ? 'currentColor' : 'none'}
                onClick={!isFavoriting ? handleFavorite : undefined}
              />
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <p>Favoritar</p>
            </TooltipContent>
          </Tooltip>
          
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <MoreVertical className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700" />
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent className="bg-black text-white">
                <p>Mais opções</p>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-48">
              {post.properties?.url && (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    window.open(post.properties?.url, '_blank', 'noopener,noreferrer')
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver Conteúdo
                </DropdownMenuItem>
              )}
              {!isInCuration && (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    handleAddDescription()
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Descrição
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  onEdit()
                }}
              >
                <Pen className="w-4 h-4 mr-2" />
                Editar Curadoria
              </DropdownMenuItem>
              {status === 'rejected' && !isInCuration && (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    setIsApproveModalOpen(true)
                  }}
                  disabled={isApproving}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Aprovar Conteúdo
                </DropdownMenuItem>
              )}
              {status === 'pending' && !isInCuration && (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    setIsRejectModalOpen(true)
                  }}
                  disabled={isRejecting}
                >
                  <CircleX className="w-4 h-4 mr-2" />
                  Rejeitar Conteúdo
                </DropdownMenuItem>
              )}
              {!isInCuration && (
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault()
                    handleDelete()
                  }}
                  disabled={isDeleting}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Deletar Conteúdo
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TooltipProvider>
      <RejectModal
        open={isRejectModalOpen}
        onOpenChange={setIsRejectModalOpen}
        onConfirm={handleRejectConfirm}
        isRejecting={isRejecting}
      />
      {status === 'rejected' && (
        <ApprovePostModal
          open={isApproveModalOpen}
          onOpenChange={setIsApproveModalOpen}
          post={post}
          onApprove={handleApprove}
        />
      )}
    </div>
  )
}
